import React from 'react';
import { useAbility } from '@casl/react';
import { ArrowRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { cn } from '../../components/ui/utils';

/**
 * "Eng & Permissions" reference page (LEARNING.md §13.6).
 *
 * Static documentation, not product UI — kept in-app so a reviewer can walk the module
 * without leaving it. It records v1 as built: SCORM deferred, PPTX download + self-attest,
 * certificates rendered client-side, and Awards on its own My Learning tab.
 */

type GrantValue = 'yes' | 'no' | 'reports';

const GRANT_LABEL: Record<GrantValue, string> = {
    yes: '✓',
    reports: '✓ reports only',
    no: '—',
};

const GRANT_CLASS: Record<GrantValue, string> = {
    yes: 'text-emerald-600',
    reports: 'text-emerald-600 text-xs',
    no: 'text-muted-foreground',
};

interface GrantRow {
    permission: string;
    employee: GrantValue;
    hrAdmin: GrantValue;
    manager: GrantValue;
}

const WALKTHROUGH: { role: string; label: string }[] = [
    {
        role: 'Employee',
        label: 'My Learning: assigned courses with their due dates, self-enrolment from the library, reading a PDF inline or downloading a PPTX, marking a course complete, and downloading certificates.',
    },
    {
        role: 'HR Admin',
        label: 'Course Library: categories and courses, publish / archive, assignments to people or departments, and the Learning Register with its export.',
    },
    {
        role: 'Manager',
        label: 'Learning Register, reports only: progress and overdue for direct reports, plus the read-only Learning tab on a direct report’s profile.',
    },
];

const COURSE_STATES: { name: string; description: string }[] = [
    { name: 'Draft', description: 'Not visible · not assignable · deletable' },
    { name: 'Active', description: 'Visible subject to CourseVisibility · assignable' },
    { name: 'Archived', description: 'Hidden · existing assignments unaffected' },
];

const GRANT_MATRIX: GrantRow[] = [
    { permission: 'read:course_enrollment', employee: 'yes', hrAdmin: 'yes', manager: 'yes' },
    { permission: 'create:course_enrollment', employee: 'yes', hrAdmin: 'yes', manager: 'yes' },
    { permission: 'download:course_certificate (own)', employee: 'yes', hrAdmin: 'yes', manager: 'yes' },
    { permission: 'read / create / update / delete:course', employee: 'no', hrAdmin: 'yes', manager: 'no' },
    { permission: 'create / update / delete:course_category', employee: 'no', hrAdmin: 'yes', manager: 'no' },
    { permission: 'read:course_assignment', employee: 'no', hrAdmin: 'yes', manager: 'reports' },
    { permission: 'create:course_assignment', employee: 'no', hrAdmin: 'yes', manager: 'no' },
    { permission: 'update:course_assignment (add/remove people)', employee: 'no', hrAdmin: 'yes', manager: 'no' },
    { permission: 'delete:course_assignment (Assigned only)', employee: 'no', hrAdmin: 'yes', manager: 'no' },
    { permission: 'read:person_learning', employee: 'no', hrAdmin: 'yes', manager: 'reports' },
];

const ENTITIES: { name: string; fields: string }[] = [
    {
        name: 'Course',
        fields:
            'title, description, thumbnailUrl, category FK (exactly one), contentType SCORM|PDF|PPTX, contentFileUrl/Key, status, showInOnboarding, issuesCertificate, estimatedDurationMinutes, passScorePercent, createdBy + audit set',
    },
    {
        name: 'CourseVisibility',
        fields:
            'course FK, visibilityType Everyone|Department|JobRole, departmentCode, jobRole — a scoping join table shaped like VendorModuleVisibility so rules stack',
    },
    {
        name: 'CourseCategory',
        fields: 'name (unique), colour, archived — archiving only removes it from the picker',
    },
    {
        name: 'CourseAssignment',
        fields:
            'course, user, assignmentType, enrollmentSource, assignedBy, assignedDate, dueDate, startedDate, completedDate, status, progressPercent, scorePercent, isOnboardingCourse + issuesCertificate (snapshots), certificateIssued',
    },
    {
        name: 'CourseCertificate',
        fields:
            'courseAssignment FK (unique), certificateNumber, issuedDate, expiryDate — the record is server-authoritative; the PDF is rendered on demand client-side (jspdf) and never stored in S3',
    },
    {
        name: 'Audit',
        fields:
            'Standard __userId / __logDescription on save via ActivityLogSubscriber — no bespoke history table',
    },
];

const COMPONENTS: { component: string; status: 'Reused' | 'New'; note: string }[] = [
    {
        component: 'Assignment creation flow',
        status: 'Reused',
        note: 'policy-new-assign.tsx — course swapped for policy',
    },
    {
        component: 'Add / Remove people modal',
        status: 'Reused',
        note: 'AddUsersToPolicyModal.tsx in add|remove mode — one component, not two',
    },
    {
        component: 'Assignment detail + progress strip',
        status: 'Reused',
        note: 'policy-assign.tsx layout',
    },
    {
        component: 'PageHeader · MetricCard · Combobox · Can / AccessRestricted · LoadingPageSkeleton · Table · Dialog · Tabs',
        status: 'Reused',
        note: 'Existing ui/ + common/ primitives',
    },
    {
        component: 'CourseCard',
        status: 'New',
        note: 'The one genuinely catalogue-shaped surface in the app — thumbnail, pills, progress/CTA',
    },
    {
        component: 'CoursePlayerShell',
        status: 'New',
        note: 'Chrome + content-type body; nothing existing plays content',
    },
    {
        component: 'LandingPagePreview',
        status: 'New',
        note: 'Live card preview inside the course form — explicitly requested',
    },
    {
        component: 'courseAssignmentStatusPillClass()',
        status: 'New',
        note: "Helper in the module's utils file, per the house rule against inline colour switches",
    },
];

const OPEN_QUESTIONS: { ref: string; title: string; body: string }[] = [
    {
        ref: '§12.1 SCORM runtime',
        title: 'Implemented',
        body: 'A shim exposing both window.API (1.2) and window.API_1484_11 (2004) is injected server-side into the package the API serves, so the content reports its own completion and score and the course completes through the ordinary progress path. Packages are unpacked on upload; suspend/resume is deliberately not stored.',
    },
    {
        ref: '§12.2 PPTX preview',
        title: 'Download + self-attest',
        body: 'The PPTX body is a download card with honour-based completion. Server-side conversion would make it identical to the PDF body and remove the variant.',
    },
    {
        ref: '§12.4 Awards placement',
        title: 'Its own tab',
        body: 'Certificates live on an "Awards & Certificates" tab beside Browse Library, so they are one click away without needing a route of their own. The prototype drew this as a section below the tabs.',
    },
    {
        ref: '§12.5 Visibility fallback',
        title: 'Assignable but hidden',
        body: 'Zero visibility rules means HR can still assign the course while it stays out of every Browse Library. Enforced server-side; the prototype inline note was dropped on review, so there is currently no publish-time warning.',
    },
    {
        ref: '§12.6 Delete when Completed',
        title: 'Completed rows locked',
        body: 'Only Assigned rows are deletable; a started row is a training record. Flipping the rule is a one-line change to the enabling condition.',
    },
    {
        ref: '§12.7 Certificate expiry',
        title: 'Informational',
        body: 'Modelled per-course (Fire Warden = 12 months) and shown amber on the certificate card. No re-assignment scheduler — that would need a scheduled job, not UI.',
    },
];

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">{title}</h2>
        {children}
    </section>
);

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
);

const FlowArrow: React.FC<{ label: string }> = ({ label }) => (
    <>
        <ArrowRight size={14} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label}</span>
        <ArrowRight size={14} className="text-muted-foreground" />
    </>
);

export default function LearningBriefPage() {
    const ability = useAbility(AbilityContext);

    if (!ability.can(ACTIONS.READ, RESOURCES.LEARNING_ADMIN)) return <AccessRestricted />;

    return (
        <div className="flex max-w-4xl flex-col gap-7 p-2 pb-10 md:p-0">
            {/* ─── Header ─── */}
            <div className="border-l-4 border-foreground pl-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Reference · /learning-admin/_brief
                </div>
                <h1 className="mt-2 text-2xl font-bold">
                    Learning — Engineering &amp; Permissions brief
                </h1>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    Documentation surface, not product UI. Everything a reviewer needs while walking the
                    module: both lifecycles, the CASL grant matrix, the entity summary, and the technical
                    decisions that shaped v1.
                </p>
            </div>

            {/* ─── 0 · Role walkthrough ─── */}
            <Section title="0 · Role walkthrough">
                <p className="text-sm leading-relaxed text-muted-foreground">
                    Each role meets the same module through a different gate — the sidebar entry and every
                    gated control re-render as that role sees them.
                </p>
                <div className="flex flex-col gap-2">
                    {WALKTHROUGH.map((step, index) => (
                        <div
                            key={step.role}
                            className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3.5 sm:flex-row sm:items-center sm:gap-3"
                        >
                            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                                {index + 1}
                            </span>
                            <span className="w-20 flex-none text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {step.role}
                            </span>
                            <span className="flex-1 text-sm">{step.label}</span>
                        </div>
                    ))}
                </div>
            </Section>

            {/* ─── 1 · Course lifecycle ─── */}
            <Section title="1 · Course lifecycle (Course Library)">
                <Card className="gap-3 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">Draft</Badge>
                        <FlowArrow label="publish" />
                        <Badge variant="success">Active</Badge>
                        <FlowArrow label="archive" />
                        <Badge className="border-amber-300 bg-amber-50 text-amber-600">Archived</Badge>
                    </div>
                    <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
                        <p>
                            Draft can be edited freely and is not visible to learners. It is the only state
                            that can be deleted, and only while it has zero assignments.
                        </p>
                        <p>
                            Active appears in Browse Library subject to its CourseVisibility rules, can be
                            assigned, and can be self-enrolled.
                        </p>
                        <p>
                            Archived disappears from the library; existing assignments and certificates stay
                            valid and read-only.
                        </p>
                    </div>
                </Card>
                <div className="grid gap-3 sm:grid-cols-3">
                    {COURSE_STATES.map((state) => (
                        <Card key={state.name} className="gap-1 p-3.5">
                            <div className="text-sm font-semibold">{state.name}</div>
                            <div className="text-xs text-muted-foreground">{state.description}</div>
                        </Card>
                    ))}
                </div>
            </Section>

            {/* ─── 2 · Assignment lifecycle ─── */}
            <Section title="2 · Assignment lifecycle (per learner, per course)">
                <Card className="gap-3 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">Assigned</Badge>
                        <FlowArrow label="learner opens" />
                        <Badge className="border-amber-300 bg-amber-50 text-amber-600">In Progress</Badge>
                        <FlowArrow label="finishes" />
                        <Badge variant="success">Completed</Badge>
                    </div>
                    <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
                        <p>
                            A row is deletable only while it is Assigned. Once the learner opens it, it becomes
                            a training record and the row is locked.
                        </p>
                        <p>
                            The certificate is issued at Completed, if the course issues one. Overdue is a
                            display state, never a status: an Assigned or In Progress row that is past its due
                            date.
                        </p>
                        <p>
                            Self-enrolment writes the same <Code>CourseAssignment</Code> row with{' '}
                            <Code>enrollmentSource: 'Self'</Code> and no due date — one table drives both the
                            Register and My Learning.
                        </p>
                    </div>
                </Card>
            </Section>

            {/* ─── 3 · CASL grant matrix ─── */}
            <Section title="3 · CASL grant matrix">
                <Card className="overflow-hidden p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/30">
                                <TableHead className="px-3 text-xs uppercase tracking-wider text-muted-foreground">
                                    Permission
                                </TableHead>
                                <TableHead className="px-3 text-center text-xs uppercase tracking-wider text-muted-foreground">
                                    Employee
                                </TableHead>
                                <TableHead className="px-3 text-center text-xs uppercase tracking-wider text-muted-foreground">
                                    HR Admin
                                </TableHead>
                                <TableHead className="px-3 text-center text-xs uppercase tracking-wider text-muted-foreground">
                                    Manager
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {GRANT_MATRIX.map((row) => (
                                <TableRow key={row.permission}>
                                    <TableCell className="px-3 font-mono text-xs">{row.permission}</TableCell>
                                    <TableCell className={cn('px-3 text-center', GRANT_CLASS[row.employee])}>
                                        {GRANT_LABEL[row.employee]}
                                    </TableCell>
                                    <TableCell className={cn('px-3 text-center', GRANT_CLASS[row.hrAdmin])}>
                                        {GRANT_LABEL[row.hrAdmin]}
                                    </TableCell>
                                    <TableCell className={cn('px-3 text-center', GRANT_CLASS[row.manager])}>
                                        {GRANT_LABEL[row.manager]}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
                <p className="text-sm leading-relaxed text-muted-foreground">
                    “Reports only” is a service-layer filter, not a CASL condition. <Code>learning_admin</Code>{' '}
                    is the category-level gate on the HR sidebar group. New RESOURCES:{' '}
                    <Code>
                        learning_admin, course, course_category, course_assignment, course_enrollment,
                        course_certificate, person_learning
                    </Code>{' '}
                    — mirrored in backend and client constants. No new ACTIONS needed.
                </p>
            </Section>

            {/* ─── 4 · Entities ─── */}
            <Section title="4 · Entities">
                <div className="grid gap-3 sm:grid-cols-2">
                    {ENTITIES.map((entity) => (
                        <Card key={entity.name} className="gap-1.5 p-3.5">
                            <div className="text-sm font-semibold">{entity.name}</div>
                            <div className="text-xs leading-relaxed text-muted-foreground">
                                {entity.fields}
                            </div>
                        </Card>
                    ))}
                </div>
            </Section>

            {/* ─── 5 · Technical decisions ─── */}
            <Section title="5 · Technical decisions (decided for v1)">
                <div className="flex flex-col gap-3">
                    <Card className="gap-2 p-4">
                        <div className="flex items-center gap-2">
                            <Badge variant="success">Decided</Badge>
                            <span className="text-sm font-semibold">SCORM — runtime implemented</span>
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            A SCORM package is a zip of HTML/JS expecting a runtime API —{' '}
                            <Code>window.API</Code> for 1.2, <Code>window.API_1484_11</Code> for 2004 —
                            injected before the content loads, which the package calls to report{' '}
                            <Code>cmi.core.lesson_status</Code>, score and suspend/resume state. Uploading the
                            zip and iframing it tracks nothing, so the runtime is injected server-side into the
                            package the API serves, behind a short-lived token scoped to one assignment.
                            Completion and score come back through the ordinary progress path, and{' '}
                            <strong>suspend/resume is deliberately not stored</strong> — a learner restarts a
                            course rather than resuming it mid-way.
                        </p>
                    </Card>
                    <Card className="gap-2 p-4">
                        <div className="flex items-center gap-2">
                            <Badge variant="success">Decided</Badge>
                            <span className="text-sm font-semibold">PPTX — download + self-attest</span>
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            No browser renders PPTX natively. The player offers a download and honours-based
                            completion: “Download to view”, then “Mark as Complete”. No server-side converter
                            and no new file-processing dependency. If conversion is added later this variant
                            collapses into the PDF body.
                        </p>
                    </Card>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                        PDF is the easy case: upload, render inline, “Mark as Complete”. One shared shell
                        (title, progress, exit) takes a content-type-specific body, so any future SCORM or PPTX
                        outcome drops in without a redesign. Certificates are the other deviation from the
                        original sketch — they are rendered client-side with <Code>jspdf</Code> from the
                        server-authoritative record (number, issued and expiry dates), so no generated files
                        accumulate in S3.
                    </p>
                </div>
            </Section>

            {/* ─── 6 · Component inventory ─── */}
            <Section title="6 · Component inventory">
                <Card className="overflow-hidden p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/30">
                                <TableHead className="px-3 text-xs uppercase tracking-wider text-muted-foreground">
                                    Component
                                </TableHead>
                                <TableHead className="px-3 text-xs uppercase tracking-wider text-muted-foreground">
                                    Status
                                </TableHead>
                                <TableHead className="px-3 text-xs uppercase tracking-wider text-muted-foreground">
                                    Note
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {COMPONENTS.map((item) => (
                                <TableRow key={item.component}>
                                    <TableCell className="px-3 text-sm">{item.component}</TableCell>
                                    <TableCell className="px-3">
                                        <span
                                            className={cn(
                                                'text-xs font-medium',
                                                item.status === 'New'
                                                    ? 'text-blue-600'
                                                    : 'text-emerald-600'
                                            )}
                                        >
                                            {item.status}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-3 text-sm text-muted-foreground">
                                        {item.note}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </Section>

            {/* ─── 7 · Where the open questions landed ─── */}
            <Section title="7 · Where the open questions landed">
                <div className="flex flex-col gap-2">
                    {OPEN_QUESTIONS.map((item) => (
                        <div key={item.ref} className="rounded-lg border border-border bg-card p-3.5">
                            <div className="text-sm">
                                <strong>{item.ref}</strong>
                                <span className="text-muted-foreground"> — {item.title}</span>
                            </div>
                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}
